// ============================================
// File: components/jobs/JobDetailsDialog.jsx
// ============================================
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Paper,
  Avatar,
} from "@mui/material"
import {
  Close as CloseIcon,
  Comment as CommentIcon,
  TrendingUp as TrendingUpIcon,
  SwapHoriz as SwapHorizIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material"

const JobDetailsDialog = ({ open, onClose, job }) => {
  const getStatusColor = (status) => {
    const statusColors = {
      completed: "success",
      processing: "primary",
      new: "warning",
      cancelled: "error",
      closed: "default",
    }
    return statusColors[status] || "default"
  }

  const getSubStatusLabel = (subStatus) => {
    const labels = {
      documentsPending: "Documents Pending",
      taxPending: "Tax Pending",
      verificationPending: "Verification Pending",
      clientNotResponding: "Client Not Responding",
      keptOnHold: "Kept On Hold",
    }
    return labels[subStatus] || subStatus
  }

  const getStatusChangeIcon = (type) => {
    if (type === "status") return <TrendingUpIcon fontSize="small" />
    if (type === "subStatus") return <SwapHorizIcon fontSize="small" />
    return <CommentIcon fontSize="small" />
  }

  const getAvatarColor = (type, to) => {
    if (type === "status") {
      if (to === "completed") return "#4caf50"
      if (to === "processing") return "#2196f3"
      if (to === "cancelled") return "#f44336"
      return "#ff9800"
    }
    return "#9e9e9e"
  }

  const formatStatusChange = (entry) => {
    const fromText = entry.from || "None"
    const toText = entry.to

    if (entry.type === "subStatus") {
      return {
        from: getSubStatusLabel(fromText),
        to: getSubStatusLabel(toText),
      }
    }
    return {
      from: fromText.charAt(0).toUpperCase() + fromText.slice(1),
      to: toText.charAt(0).toUpperCase() + toText.slice(1),
    }
  }

  if (!job) return null

  // Support both _id and JobId formats
  const jobId = job._id || job.JobId

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h5" component="div">
          Job Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Box>
          {/* Job Overview Card */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={1}>
                {/* Row 1: Job ID, Client, Job Type */}
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Job ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {jobId}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Client
                  </Typography>
                  <Typography variant="body2">
                    {job.clientName || "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Job Type
                  </Typography>
                  <Typography variant="body2">
                    {job.jobType || "N/A"}
                  </Typography>
                </Grid>

                {/* Row 2: Assigned, Due Date, Fees */}
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Assigned To
                  </Typography>
                  <Typography variant="body2">
                    {job.assignedToName || "Unassigned"}
                  </Typography>
                  {job.secondaryUser && job.secondaryUser.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                      + {job.secondaryUser.map(u => u.name).join(", ")}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body2">
                    {job.dueDate
                      ? new Date(job.dueDate).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Fees
                  </Typography>
                  <Typography variant="body2">
                    ₹{job.fees?.toLocaleString("en-IN") || 0}
                  </Typography>
                </Grid>

                {/* Row 3: Status + Substatus + Created At */}
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.3 }}>
                    <Chip
                      label={job.status}
                      color={getStatusColor(job.status)}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                  {job.status === 'completed' ? "Fee Status" : "Sub Status"}
                  </Typography>
                  {
                    job.status === 'completed' ?  
                    (<Box sx={{ mt: 0.3 }}>
                      <Typography variant="body2" color="text.secondary">
                      {job.feeStatus}
                      </Typography>
                    </Box>)
                    : (<Box sx={{ mt: 0.3 }}>
                    {job.subStatus ? (
                      <Chip
                        label={getSubStatusLabel(job.subStatus)}
                        variant="outlined"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </Box>)
                  }
                </Grid>

                {/* Created At */}
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2">
                    {new Date(job.createdAt).toLocaleString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Grid>
                {/* Created By */}
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body2">
                    {job?.createdBy?.name || ''}
                  </Typography>
                </Grid>

                {/* Description (optional) */}
                {job.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {job.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Status History & Comments */}
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <CommentIcon fontSize="small" />
                Status History & Comments
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* General Comments Section */}
              {job.comments && job.comments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    Comments ({job.comments.length})
                  </Typography>
                  <Stack spacing={1}>
                    {job.comments.map((comment, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 1.2,
                          bgcolor: "action.hover",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="body2" fontWeight={500}>
                            {comment.commentedBy?.name || "Unknown User"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: "nowrap", ml: 1 }}
                          >
                            {new Date(comment.commentedAt).toLocaleString(
                              "en-IN",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.2,
                            lineHeight: 1.3,
                          }}
                        >
                          {comment.commentText}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                </Box>
              )}

              {/* Status History Timeline */}
              <Typography
                variant="subtitle2"
                color="primary"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                Status Change History
              </Typography>

              {job.statusHistory && job.statusHistory.length > 0 ? (
                <Stack spacing={1.5}>
                  {job.statusHistory.map((entry, index) => {
                    const statusChange = formatStatusChange(entry)
                    return (
                      <Box
                        key={entry._id || index}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          position: "relative",
                          "&:not(:last-child)::after": {
                            content: '""',
                            position: "absolute",
                            left: "16px",
                            top: "36px",
                            bottom: "-16px",
                            width: "1.5px",
                            bgcolor: "divider",
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: getAvatarColor(entry.type, entry.to),
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                          }}
                        >
                          {getStatusChangeIcon(entry.type)}
                        </Avatar>

                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "baseline",
                              mb: 0.3,
                            }}
                          >
                            <Box>
                              <Chip
                                label={
                                  entry.type === "status"
                                    ? "Status"
                                    : "Sub-Status"
                                }
                                size="small"
                                color={
                                  entry.type === "status" ? "primary" : "default"
                                }
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 20,
                                  fontWeight: 500,
                                }}
                              />
                              {entry.by?.name && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  By: {entry.by.name}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(entry.at).toLocaleString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </Box>

                          <Paper
                            elevation={0}
                            sx={{
                              p: 1,
                              bgcolor: "action.hover",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mb: entry.reason ? 0.8 : 0,
                              }}
                            >
                              <Chip
                                label={statusChange.from}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                              <ArrowForwardIcon
                                fontSize="small"
                                sx={{ fontSize: "1rem" }}
                              />
                              <Chip
                                label={statusChange.to}
                                size="small"
                                color={
                                  entry.type === "status"
                                    ? getStatusColor(entry.to)
                                    : "default"
                                }
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            </Box>

                            {entry.reason && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 0.3,
                                  color: "text.secondary",
                                  lineHeight: 1.3,
                                }}
                              >
                                "{entry.reason}"
                              </Typography>
                            )}
                          </Paper>
                        </Box>
                      </Box>
                    )
                  })}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  No status changes recorded yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default JobDetailsDialog